import {useEffect, useState} from 'react';
import {Checkbox, FormControlLabel, Grid, MenuItem} from '@mui/material';
import {BusinessNatureFields, ExpansionPanel, InputField, SnackbarField} from '@/components/common';
import {DatePicker} from '@/components/common/DatePicker';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';

type Props = {
    annualId: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
};

export default function EpBusiness({annualId, onChange}: Props) {
    const store = useCompanyAnnualStore();
    const [local, setLocal] = useState<{
        principalActivities?: number;
        businessNature: string | null;
        commenceWithinPeriod: number;
        commenceDate: string | null;
    }>({
        principalActivities: undefined,
        businessNature: null,
        commenceWithinPeriod: 1,
        commenceDate: null,
    });
    const [periodBounds, setPeriodBounds] = useState<{ periodFrom: string | null; periodTo: string | null }>({
        periodFrom: null,
        periodTo: null,
    });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>({
        open: false,
        message: '',
        severity: 'error',
    });

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!annualId) return;
            const [v, periodInfo] = await Promise.all([
                store.getBusinessInfo(annualId),
                store.getPeriodInfo(annualId),
            ]);
            if (!mounted) return;
            const commenceWithinPeriod = v.commenceWithinPeriod ?? 1;
            const defaultCommenceDate = commenceWithinPeriod === 0 ? null : (v.commenceDate ?? periodInfo.periodFrom ?? null);
            setPeriodBounds({
                periodFrom: periodInfo.periodFrom ?? null,
                periodTo: periodInfo.periodTo ?? null,
            });
            setLocal({
                principalActivities: v.principalActivities,
                businessNature: v.businessNature,
                commenceWithinPeriod,
                commenceDate: defaultCommenceDate,
            });
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, store]);

    const emit = (patch: Partial<typeof local>) => {
        const next = {...local, ...patch};
        setLocal(next);
        onChange?.(patch as Partial<CompanyAnnual>);
    };

    return (
        <ExpansionPanel title="Business (業務)">
            <Grid container spacing={0.5}>
                <Grid size={6 as any}>
                    <InputField select fullWidth variant="outlined" label="Principal activities (主營業務)"
                                value={local.principalActivities ?? ''}
                                onChange={(e) => emit({principalActivities: Number(e.target.value) as any})}>
                        <MenuItem dense value="">Select</MenuItem>
                        <MenuItem dense value={0}>Inactive (不活躍)</MenuItem>
                        <MenuItem dense value={1}>General Trading (一般貿易)</MenuItem>
                        <MenuItem dense value={2}>Services (服務)</MenuItem>
                        <MenuItem dense value={3}>Trading and Service (貿易與服務)</MenuItem>
                    </InputField>
                </Grid>
                <Grid size={12 as any}>
                    <BusinessNatureFields
                        value={local.businessNature}
                        onChange={(combined) => emit({businessNature: combined})}
                        inactivePrincipal={Number(local.principalActivities) === 0}
                        showFieldLabels
                        onError={(message) => setSnackbar({open: true, message, severity: 'error'})}
                    />
                </Grid>
                <Grid size={6}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={local.commenceWithinPeriod !== 0}
                                onChange={(_, checked) => emit({
                                    commenceWithinPeriod: checked ? 1 : 0,
                                    commenceDate: checked ? (local.commenceDate ?? periodBounds.periodFrom ?? null) : null,
                                })}
                            />
                        }
                        label="Commence business within this basis period？"
                    />
                </Grid>
                {local.commenceWithinPeriod !== 0 && (
                    <Grid size={6 as any}>
                        <DatePicker
                            label="commence Date"
                            value={local.commenceDate ?? periodBounds.periodFrom ?? null}
                            onChange={(value) => emit({commenceDate: value})}
                            fromDate={periodBounds.periodFrom ?? undefined}
                            toDate={periodBounds.periodTo ?? undefined}
                            align='left'
                            fullWidth
                        />
                    </Grid>
                )}
            </Grid>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
            />
        </ExpansionPanel>
    );
}
